/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactElement, type ReactNode, useMemo, useState, lazy, Suspense } from 'react';
import type { ShopperProducts } from '@/scapi';
import ProductQuantityPicker from '@/components/product-quantity-picker';
import { SwatchGroup, Swatch } from '@/components/swatch-group';
// RGBARRY-CUSTOMIZATION: ColorSwatchCarousel/FabricPills below render their "uncontrolled" swatch
// links with this site-context-aware Link, not a raw <a href>, so URLs get the site/locale prefix.
import { Link } from '@/components/link';
import { useVariationAttributes } from '@/hooks/product/use-variation-attributes';
import { useOptionalProductView } from '@/providers/product-view';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import ProductPrice from '../product-price';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import InventoryMessage, { InventoryStatus } from '../inventory-message';
// @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
import { ProductRatingSummary } from './product-rating-summary';
// @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
import { useCurrentVariant } from '@/hooks/product/use-current-variant';
import { useTranslation } from 'react-i18next';
import { WishlistButton } from '@/components/buttons/wishlist-button';
import { ShareButton } from '@/components/buttons/share-button';
import { UITarget } from '@/targets/ui-target';
// @sfdc-extension-line SFDC_EXT_BOPIS
// RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP
// import DeliveryOptions from '@/extensions/bopis/components/delivery-options/delivery-options';

const CompareFabricsModal = lazy(() => import('@/components/compare-fabrics-modal'));

type ProductInfoBaseProps = {
    product: ShopperProducts.schemas['Product'];
    hideVariantSelection?: boolean;
    /** Layout style: 'full' (default) shows title, description, inventory; 'compact' shows brand, smaller title, sorted attributes */
    variantStyle?: 'full' | 'compact';
    /** When true and mode is 'edit', show quantity picker (e.g. in cart edit modal) */
    showQuantityInEditMode?: boolean;
    // RGBARRY-CUSTOMIZATION: PDP hides the standalone quantity picker — quantity is chosen via the
    // in-cart stepper that the Add to Cart button becomes after the item is added (see ProductCartActions)
    /** When true, never render the standalone quantity picker (used on the PDP) */
    hideQuantityPicker?: boolean;
    // END RGBARRY-CUSTOMIZATION
    /** Optional current variant from parent orchestration (used by controlled modal flows) */
    currentVariantOverride?: ShopperProducts.schemas['Variant'];
    /** Whether selected variant inventory is currently being fetched */
    isVariantInventoryLoading?: boolean;
    /** Hide top-right action icons (wishlist/share) */
    hideActionIcons?: boolean;
    /** Optional action content rendered inline with title in full variant style */
    headerAction?: ReactNode;
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    /** Disable rating summary interactions (hover popover and review links) */
    disableRatingInteraction?: boolean;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
};
type ProductInfoUncontrolledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'uncontrolled' uses URL navigation */
    swatchMode?: 'uncontrolled';
    onAttributeChange?: never;
    variationValues?: never;
};
type ProductInfoControlledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'controlled' uses callback */
    swatchMode: 'controlled';
    /** Callback when variant attribute changes in controlled mode */
    onAttributeChange: (attributeId: string, value: string) => void;
    /** Controlled variation values for controlled mode (e.g., {color: 'red', size: 'M'}) */
    variationValues: { [key: string]: string };
};
type ProductInfoProps = ProductInfoUncontrolledProps | ProductInfoControlledProps;

const isControlledVariantValueOrderable = ({
    variants,
    currentSelection,
    attributeId,
    attributeValue,
}: {
    variants: ShopperProducts.schemas['Variant'][] | undefined;
    currentSelection: Record<string, string>;
    attributeId: string;
    attributeValue: string;
}): boolean => {
    if (!variants || variants.length === 0) {
        return true;
    }

    const nextSelection = {
        ...currentSelection,
        [attributeId]: attributeValue,
    };

    return variants
        .filter((variant) =>
            Object.entries(nextSelection).every(([key, value]) => variant.variationValues?.[key] === value)
        )
        .some((variant) => variant.orderable);
};

function ColorSwatchCarousel({
    name,
    activeValue,
    activeDisplayName,
    swatchesToShow,
    swatchMode,
    product,
    variationValues,
    config,
    onAttributeChange,
    id,
}: {
    name: string;
    activeValue: string | undefined;
    activeDisplayName: string;
    swatchesToShow: ReturnType<typeof useVariationAttributes>[number]['values'];
    swatchMode: 'uncontrolled' | 'controlled';
    product: ShopperProducts.schemas['Product'];
    variationValues?: Record<string, string>;
    config: AppConfig;
    onAttributeChange?: (attributeId: string, value: string) => void;
    id: string;
}) {
    return (
        <div className="space-y-3 w-full min-w-0">
            <div className="text-sm text-[#1a1a1a]">
                <span className="font-bold">Selected Style: </span>
                <span>{activeDisplayName}</span>
            </div>
            {/* RGBARRY-CUSTOMIZATION: horizontal scroll on mobile when many color swatches */}
            <div
                className="flex w-full overflow-x-auto p-1 -m-1 md:flex-wrap md:overflow-x-hidden md:p-1 md:m-0 gap-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                role="radiogroup"
                aria-label={name}>
                {swatchesToShow.map((value) => {
                    const { href, name: valueName, image, value: swatchValue, orderable } = value;
                    const isOrderable =
                        swatchMode === 'controlled'
                            ? isControlledVariantValueOrderable({
                                  variants: product.variants,
                                  currentSelection: variationValues ?? {},
                                  attributeId: id,
                                  attributeValue: swatchValue,
                              })
                            : (orderable ?? true);
                    const isSelected = activeValue === swatchValue;
                    const swatchImageUrl = image ? toImageUrl({ image, config }) || '' : '';
                    // RGBARRY-CUSTOMIZATION: no overflow-hidden on outer so ring-offset renders unclipped;
                    // inner span clips the image to the circle shape instead.
                    const commonClass = [
                        'relative w-8 h-8 rounded-full shrink-0 transition-all duration-150',
                        isSelected
                            ? 'ring-2 ring-[#1a1a1a] ring-offset-2'
                            : 'ring-1 ring-[#d0d0d8] hover:ring-[#999] ring-offset-0',
                        isOrderable ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed',
                    ].join(' ');
                    const imageContent = (
                        <span className="absolute inset-0 rounded-full overflow-hidden block">
                            {swatchImageUrl ? (
                                <img
                                    src={swatchImageUrl}
                                    alt={image?.alt || valueName}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span className="w-full h-full block bg-[#ccc]" />
                            )}
                        </span>
                    );
                    if (href && swatchMode === 'uncontrolled') {
                        return (
                            <Link
                                key={swatchValue}
                                to={href}
                                aria-label={valueName}
                                aria-current={isSelected ? 'true' : undefined}
                                className={commonClass}>
                                {imageContent}
                            </Link>
                        );
                    }
                    return (
                        <button
                            key={swatchValue}
                            type="button"
                            aria-label={valueName}
                            aria-pressed={isSelected}
                            disabled={!isOrderable}
                            onClick={() => onAttributeChange?.(id, swatchValue)}
                            className={commonClass}>
                            {imageContent}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function FabricPills({
    name,
    activeValue,
    swatchesToShow,
    swatchMode,
    product,
    variationValues,
    onAttributeChange,
    id,
}: {
    name: string;
    activeValue: string | undefined;
    activeDisplayName: string;
    swatchesToShow: ReturnType<typeof useVariationAttributes>[number]['values'];
    swatchMode: 'uncontrolled' | 'controlled';
    product: ShopperProducts.schemas['Product'];
    variationValues?: Record<string, string>;
    onAttributeChange?: (attributeId: string, value: string) => void;
    id: string;
}) {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMounted, setModalMounted] = useState(false);

    const openModal = () => {
        setModalMounted(true);
        setModalOpen(true);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-[#1a1a1a]">{name}:</span>
                <button
                    type="button"
                    onClick={openModal}
                    className="text-xs text-[#1a1a1a] underline underline-offset-2 hover:text-[#555] transition-colors">
                    Compare Fabrics
                </button>
            </div>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={name}>
                {swatchesToShow.map((value) => {
                    const { href, name: valueName, value: swatchValue, orderable } = value;
                    const isOrderable =
                        swatchMode === 'controlled'
                            ? isControlledVariantValueOrderable({
                                  variants: product.variants,
                                  currentSelection: variationValues ?? {},
                                  attributeId: id,
                                  attributeValue: swatchValue,
                              })
                            : (orderable ?? true);
                    const isSelected = activeValue === swatchValue;
                    const pillClass = [
                        'py-3 px-4 text-sm text-center rounded-full border transition-all duration-150',
                        isSelected
                            ? 'border-[#1a1a1a] text-[#1a1a1a] font-medium'
                            : 'border-[#d0d0d8] text-[#9a9a9a] hover:border-[#888] hover:text-[#1a1a1a]',
                        isOrderable ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed',
                    ].join(' ');
                    if (href && swatchMode === 'uncontrolled') {
                        return (
                            <Link
                                key={swatchValue}
                                to={href}
                                aria-label={valueName}
                                aria-current={isSelected ? 'true' : undefined}
                                className={pillClass}>
                                {valueName}
                            </Link>
                        );
                    }
                    return (
                        <button
                            key={swatchValue}
                            type="button"
                            aria-label={valueName}
                            aria-pressed={isSelected}
                            disabled={!isOrderable}
                            onClick={() => onAttributeChange?.(id, swatchValue)}
                            className={pillClass}>
                            {valueName}
                        </button>
                    );
                })}
            </div>
            {modalMounted && (
                <Suspense fallback={null}>
                    <CompareFabricsModal open={modalOpen} onOpenChange={setModalOpen} />
                </Suspense>
            )}
        </div>
    );
}

/**
 * ProductInfo component displays product details including title, description, price, variants, and quantity picker
 *
 * Supports two swatch modes:
 * - uncontrolled mode (default): Swatches use URL navigation for variant selection
 * - controlled mode: Swatches use callbacks for controlled variant selection (used in modals)
 *
 * @param props - Component props
 * @param props.product - The product data to display
 * @param props.swatchMode - Swatch interaction mode ('uncontrolled' or 'controlled')
 * @param props.onAttributeChange - Callback for controlled mode variant changes
 * @param props.variationValues - Controlled variation values for controlled mode
 * @returns JSX element with product information display
 */
export default function ProductInfo({
    product,
    swatchMode = 'uncontrolled',
    onAttributeChange,
    variationValues,
    hideVariantSelection = false,
    variantStyle = 'full',
    showQuantityInEditMode = false,
    // RGBARRY-CUSTOMIZATION: PDP hides the standalone quantity picker (see prop docs above)
    hideQuantityPicker = false,
    // END RGBARRY-CUSTOMIZATION
    currentVariantOverride,
    isVariantInventoryLoading = false,
    hideActionIcons = false,
    headerAction,
    // @sfdc-extension-line SFDC_EXT_RATINGS_REVIEWS
    disableRatingInteraction = false,
}: ProductInfoProps): ReactElement {
    const config = useConfig();
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);
    // Use variation attributes hook for URL-aware swatches
    const variationAttributes = useVariationAttributes({ product });
    const urlCurrentVariant = useCurrentVariant({ product });
    const controlledCurrentVariant = useMemo(() => {
        if (swatchMode !== 'controlled') return undefined;
        if (!variationValues) return undefined;

        const potentialVariants =
            product.variants?.filter((variant) =>
                Object.keys(variationValues).every((key) => variant.variationValues?.[key] === variationValues[key])
            ) ?? [];
        return potentialVariants.length === 1 ? potentialVariants[0] : undefined;
    }, [swatchMode, product.variants, variationValues]);
    // For controlled modal flows, prefer explicit override (can include fetched inventory),
    // then controlled selection, then URL-based variant as fallback.
    const currentVariant = currentVariantOverride || controlledCurrentVariant || urlCurrentVariant;
    const productForPrice = useMemo(() => {
        if (!currentVariant) return product;
        // Build a variant-like product shape so ProductPrice does not treat it as master range pricing.
        return {
            ...product,
            ...currentVariant,
            type: { ...(product.type ?? {}), master: false, variant: true },
            variants: undefined,
        } as ShopperProducts.schemas['Product'];
    }, [product, currentVariant]);
    // RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP
    // const productForDeliveryOptions = useMemo(() => {
    //     if (!currentVariant) return product;
    //     const variantWithInventory = currentVariant as ShopperProducts.schemas['Variant'] & {
    //         inventory?: ShopperProducts.schemas['Inventory'];
    //         inventories?: ShopperProducts.schemas['Inventory'][];
    //     };
    //     return {
    //         ...product,
    //         inventory: variantWithInventory.inventory ?? product.inventory,
    //         inventories: variantWithInventory.inventories ?? product.inventories,
    //     };
    // }, [product, currentVariant]);
    // Get currency from context (automatically derived from locale)
    const { currency } = useSite();
    const productView = useOptionalProductView();
    const [standaloneQuantity, setStandaloneQuantity] = useState(1);
    const quantity = productView?.quantity ?? standaloneQuantity;
    const isOutOfStock = productView?.isOutOfStock ?? product.inventory?.orderable === false;
    const stockLevel = productView?.stockLevel ?? product.inventory?.ats;
    const maxQuantity = productView?.maxQuantity;
    const setQuantity = productView?.setQuantity ?? setStandaloneQuantity;
    const mode = productView?.mode ?? 'add';
    // @sfdc-extension-line SFDC_EXT_BOPIS
    // RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP
    // const basketPickupStore = productView?.basketPickupStore;
    const { t } = useTranslation('product');

    const isCompactStyle = variantStyle === 'compact';
    // RGBARRY-CUSTOMIZATION: hideQuantityPicker suppresses the standalone picker on the PDP
    const showQuantity =
        !hideQuantityPicker && !isProductASet && !isProductABundle && (mode !== 'edit' || showQuantityInEditMode);
    // END RGBARRY-CUSTOMIZATION

    // In compact mode, sort variation attributes by priority order
    const COMPACT_ATTRIBUTE_ORDER = ['size', 'color'];
    const sortedVariationAttributes = isCompactStyle
        ? [...variationAttributes].sort((a, b) => {
              const aIndex = COMPACT_ATTRIBUTE_ORDER.indexOf(a.id);
              const bIndex = COMPACT_ATTRIBUTE_ORDER.indexOf(b.id);
              // Attributes not in the list sort to the end, preserving original order
              const aPriority = aIndex === -1 ? COMPACT_ATTRIBUTE_ORDER.length : aIndex;
              const bPriority = bIndex === -1 ? COMPACT_ATTRIBUTE_ORDER.length : bIndex;
              return aPriority - bPriority;
          })
        : variationAttributes;
    const selectedVariationValues = useMemo(() => {
        if (swatchMode === 'controlled') {
            return variationValues ?? {};
        }
        return variationAttributes.reduce<Record<string, string>>((acc, attribute) => {
            const selectedValue = attribute.selectedValue?.value;
            if (selectedValue) {
                acc[attribute.id] = selectedValue;
            }
            return acc;
        }, {});
    }, [swatchMode, variationValues, variationAttributes]);
    const shouldHideInventoryForPartialVariantSelection = useMemo(() => {
        const variants = product.variants ?? [];
        const variationAttributeCount = product.variationAttributes?.length ?? 0;
        if (variants.length === 0 || variationAttributeCount <= 1) {
            return false;
        }

        const potentialVariants = variants.filter((variant) =>
            Object.entries(selectedVariationValues).every(([key, value]) => variant.variationValues?.[key] === value)
        );

        // Keep inventory hidden until the shopper narrows selection down to a single variant.
        return potentialVariants.length !== 1;
    }, [product.variants, product.variationAttributes?.length, selectedVariationValues]);
    const inventoryStatusOverride = useMemo(() => {
        if (!isVariantInventoryLoading && !shouldHideInventoryForPartialVariantSelection) {
            return undefined;
        }
        return (
            inventoryProduct: ShopperProducts.schemas['Product'],
            inventoryVariant?: ShopperProducts.schemas['Variant'] | null
        ) => {
            if (shouldHideInventoryForPartialVariantSelection) {
                return InventoryStatus.UNKNOWN;
            }
            const hasVariants = (inventoryProduct.variants?.length ?? 0) > 0;
            const missingVariantInventory =
                (
                    inventoryVariant as
                        | (ShopperProducts.schemas['Variant'] & {
                              inventory?: ShopperProducts.schemas['Inventory'];
                          })
                        | null
                )?.inventory == null;
            // During quick-add variant fetch, hide transient inventory message
            // until selected variant inventory is available.
            if (hasVariants && missingVariantInventory) {
                return InventoryStatus.UNKNOWN;
            }
            return InventoryStatus.IN_STOCK;
        };
    }, [isVariantInventoryLoading, shouldHideInventoryForPartialVariantSelection]);

    return (
        // RGBARRY-CUSTOMIZATION: PDP info restructure — transparent action icons at top, tighter spacing
        <div className="relative grid gap-2">
            {/* Action icons — top-right */}
            {!isCompactStyle && !hideActionIcons && (
                <div className="absolute top-0 right-0 flex items-center gap-2 z-10">
                    {/* RGBARRY-CUSTOMIZATION: on mobile the wishlist heart overlays the hero image
                        (see ImageGallery heroOverlay in product-view); keep it here on desktop only. */}
                    <span className="hidden lg:contents">
                        <WishlistButton
                            product={{
                                productId: product.id,
                                productName: product.name,
                                price: product.price,
                                image: product.imageGroups?.[0]?.images?.[0],
                            }}
                            surface="pdp"
                            size="sm"
                            className="!static !bg-transparent !shadow-none border-0"
                        />
                    </span>
                    <ShareButton
                        product={product}
                        size="sm"
                        className="!static !bg-transparent !shadow-none border-0 [&_svg]:stroke-[2]"
                    />
                </div>
            )}

            {/* Compact style: brand (uppercase) then product name */}
            {isCompactStyle && (
                <>
                    {product.brand && (
                        <p className="text-xs font-normal leading-none uppercase tracking-wide text-secondary-foreground">
                            {product.brand}
                        </p>
                    )}
                    <h2 className="text-xl font-bold text-card-foreground tracking-tight">{product.name}</h2>
                </>
            )}

            {/* Product Title, SKU, Description */}
            {!isCompactStyle && (
                <div className="flex items-start justify-between gap-4">
                    <div className={`${hideActionIcons ? '' : 'pr-28'} min-w-0`}>
                        {product.brand && (
                            <p className="mb-1 text-xs font-normal leading-none uppercase tracking-wide text-secondary-foreground">
                                {product.brand}
                            </p>
                        )}
                        <h1
                            data-testid="product-title"
                            className="text-xl font-bold text-card-foreground tracking-tight">
                            {product.name}
                        </h1>
                    </div>
                    {headerAction ? <div className="pt-1 shrink-0">{headerAction}</div> : null}
                </div>
            )}
            {/* RGBARRY-CUSTOMIZATION: long description blurb under the title */}
            {!isCompactStyle && product.longDescription && (
                <p className="mt-2 text-sm text-secondary-foreground leading-relaxed">
                    {product.longDescription
                        .replace(/<[^>]*>/g, ' ')
                        .replace(/\s{2,}/g, ' ')
                        .trim()}
                </p>
            )}
            {/* Rating summary - visible on both mobile and desktop */}
            {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
            {!isCompactStyle && (
                <UITarget targetId="sfcc.pdp.reviews.rating">
                    <ProductRatingSummary interactive={!disableRatingInteraction} />
                </UITarget>
            )}
            {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}

            {/* Price - show unit price on PDP */}
            <div className="space-y-1">
                <ProductPrice
                    type="unit"
                    product={productForPrice}
                    quantity={quantity}
                    currency={currency}
                    // Match the purchasability gate: bonus/promo products (allowMissingPrice) render
                    // their price instead of "Price unavailable".
                    allowMissingPrice={productView?.allowMissingPrice}
                    labelForA11y={product?.name}
                    currentPriceProps={{
                        className: 'text-xl font-bold text-card-foreground leading-[120%] tracking-[-0.6px]',
                    }}
                    promoCalloutProps={{
                        className: 'text-sm [&_span]:mx-0 [&_span]:text-status-positive',
                    }}
                    hidePromo={isCompactStyle}
                    currentPriceOnly={isCompactStyle}
                />
            </div>

            {/* Inventory Status Message - hidden in compact/edit mode */}
            {!isCompactStyle && (
                <UITarget targetId="sfcc.pdp.shipping.deliveryEstimate">
                    <InventoryMessage
                        product={product}
                        currentVariant={currentVariant}
                        lowStockThreshold={config.global.inventory.lowStockThreshold}
                        getInventoryStatus={inventoryStatusOverride}
                    />
                </UITarget>
            )}
            {!isCompactStyle && <UITarget targetId="sfcc.pdp.loyalty.points" />}

            {/* Swatch Groups for Product Variations */}
            {sortedVariationAttributes.map(({ id, name, selectedValue, values }) => {
                const controlledValue = variationValues?.[id];
                const controlledDisplayName = controlledValue
                    ? values.find((v) => v.value === controlledValue)?.name || ''
                    : '';
                const swatchesToShow = hideVariantSelection
                    ? values.filter((v) => v.value === selectedValue?.value)
                    : values;
                const isColorAttr = id === 'color';
                const activeValue = swatchMode === 'controlled' ? controlledValue : selectedValue?.value;
                const activeDisplayName =
                    swatchMode === 'controlled' ? controlledDisplayName : selectedValue?.name || '';

                if (isColorAttr) {
                    return (
                        <ColorSwatchCarousel
                            key={id}
                            name={name}
                            activeValue={activeValue}
                            activeDisplayName={activeDisplayName}
                            swatchesToShow={swatchesToShow}
                            swatchMode={swatchMode}
                            product={product}
                            variationValues={variationValues}
                            config={config}
                            onAttributeChange={onAttributeChange}
                            id={id}
                        />
                    );
                }

                const isFabricAttr = id.toLowerCase().includes('fabric') || name.toLowerCase().includes('fabric');
                if (isFabricAttr) {
                    return (
                        <FabricPills
                            key={id}
                            name={name}
                            activeValue={activeValue}
                            activeDisplayName={activeDisplayName}
                            swatchesToShow={swatchesToShow}
                            swatchMode={swatchMode}
                            product={product}
                            variationValues={variationValues}
                            onAttributeChange={onAttributeChange}
                            id={id}
                        />
                    );
                }

                const swatches = swatchesToShow.map((value) => {
                    const { href, name: valueName, value: swatchValue, orderable } = value;
                    const isOrderable =
                        swatchMode === 'controlled'
                            ? isControlledVariantValueOrderable({
                                  variants: product.variants,
                                  currentSelection: variationValues ?? {},
                                  attributeId: id,
                                  attributeValue: swatchValue,
                              })
                            : (orderable ?? true);
                    return (
                        <Swatch
                            key={swatchValue}
                            href={swatchMode === 'uncontrolled' ? href : undefined}
                            disabled={!isOrderable}
                            value={swatchValue}
                            name={valueName}
                            shape="label"
                            labeled
                            outOfStockSuffix={t('outOfStockSuffix')}>
                            {/* RGBARRY-CUSTOMIZATION: label pill swatch content */}
                            <span className="text-xs font-medium">{valueName}</span>
                        </Swatch>
                    );
                });
                return (
                    <SwatchGroup
                        key={id}
                        value={activeValue}
                        displayName={activeDisplayName}
                        label={name}
                        handleChange={
                            hideVariantSelection
                                ? undefined
                                : swatchMode === 'controlled'
                                  ? (value) => onAttributeChange?.(id, value)
                                  : undefined
                        }>
                        {swatches}
                    </SwatchGroup>
                );
            })}
            {!isCompactStyle && <UITarget targetId="sfcc.pdp.products.visualization" />}

            {/* @sfdc-extension-block-start SFDC_EXT_BOPIS */}
            {/* RGBARRY-CUSTOMIZATION: hide BOPIS / store pickup from PDP */}
            {/* {!isOutOfStock && (mode !== 'edit' || basketPickupStore) && !(isProductABundle || isProductASet) && (
                <DeliveryOptions
                    product={productForDeliveryOptions}
                    quantity={quantity}
                    basketPickupStore={basketPickupStore}
                    className="mt-6"
                />
            )} */}
            {/* @sfdc-extension-block-end SFDC_EXT_BOPIS */}

            {/* Quantity Selector - for non-set/bundle when not edit mode, or when showQuantityInEditMode in edit mode */}
            {showQuantity && (
                <ProductQuantityPicker
                    value={quantity.toString()}
                    onChange={setQuantity}
                    stockLevel={stockLevel}
                    isOutOfStock={isOutOfStock}
                    productName={product.name}
                    maxQuantity={maxQuantity}
                />
            )}

            {/* Product Bundle/Set Notice */}
            {(isProductASet || isProductABundle) && (
                <div className="rounded-ui bg-primary/10 border border-primary p-4">
                    <p className="text-sm text-primary">
                        {isProductASet ? t('productSetNotice') : t('productBundleNotice')}
                    </p>
                </div>
            )}
        </div>
    );
}
